"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useCreatePage } from "@/hooks/useCreatePage";
import { useAuth } from "@/context/providers/AuthProvider";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import { useVirtualKeyboardInset } from "@/hooks/useVirtualKeyboardInset";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import CreateAgentLayout from "@/components/create/agent/CreateAgentLayout";
import { ChatActionsProvider } from "@/components/create/agent/ChatActionsProvider";
import { CreatePageModals } from "@/components/create/CreatePageModals";
import { CreateQuoteAccountModal } from "@/components/create/CreateQuoteAccountModal";
import Metadata from "@/components/seo/Metadata";
import { StructuredData } from "@/components/seo/SEO";
import { CUSTOM_DRESS_STARTING_PRICES } from "@/constants/serviceFacts";
import { useCreatePageStore } from "@/stores/createPageStore";
import { redirectToLoginWithDestination } from "@/hooks/useRedirectToLogin";
import { usePendingQuoteResume } from "@/hooks/create-page/hooks/usePendingQuoteResume";
import {
  hasVerifiedAccount,
  needsAnonymousWorkHandoff,
} from "@/lib/auth/accountStatus";
import { createResumeIntentKey } from "@/utils/createResumeIntentKey";
import { hasRenderableChatTranscript } from "@/lib/agent/renderableChatMessage";
import { usePendingCreditPurchaseResume } from "@/hooks/create-page/hooks/usePendingCreditPurchaseResume";
import { usePendingSubmittedMessageResume } from "@/hooks/create-page/hooks/usePendingSubmittedMessageResume";
import { resolvePendingInlineConfirmation } from "@/hooks/create-page/utils/pendingInlineConfirmation";
import {
  CREDIT_AUTH_HANDOFF_ERROR,
  prepareCreditSignInHandoff,
} from "@/utils/creditAuthHandoff";
import {
  prepareQuoteSignInHandoff,
  QUOTE_AUTH_HANDOFF_ERROR,
} from "@/utils/quoteAuthHandoff";
import { canResumeCreditGate } from "./creditGateResume";

export default function CreateDressPageContent() {
  const { session, isAnonymous, loading: authLoading } = useAuth();
  const {
    balance,
    transactions,
    isLoading: creditsLoading,
  } = useCreditsContext();
  const hasReceivedNewUserBonus = transactions.some(
    transaction =>
      transaction.source === "signup" || transaction.source === "anon_grant"
  );
  const router = useRouter();
  const isDesktopWorkspace = useMediaQuery("(min-width: 1024px)");

  // Exposes `--keyboard-inset` on <html> so the chat composer can lift above
  // the iOS on-screen keyboard (Android is handled via interactive-widget).
  useVirtualKeyboardInset();

  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState<
    boolean | null
  >(null);
  const [checkingFeedbackStatus, setCheckingFeedbackStatus] = useState(false);
  const [hasShared, setHasShared] = useState<boolean | null>(null);
  const [checkingSocialShareStatus, setCheckingSocialShareStatus] =
    useState(false);
  const [hasShareablePastDesigns, setHasShareablePastDesigns] = useState<
    boolean | null
  >(null);
  const [checkingShareablePastDesigns, setCheckingShareablePastDesigns] =
    useState(false);
  const [showSocialShareModal, setShowSocialShareModal] = useState(false);
  const [ticketCount, setTicketCount] = useState<number | null>(null);
  const [checkingTicketCount, setCheckingTicketCount] = useState(false);
  const [checkoutRecoveryCredits, setCheckoutRecoveryCredits] = useState<
    number | null
  >(null);

  const pendingConfirmationHref =
    typeof window === "undefined" ? "" : window.location.href;
  const pendingInlineConfirmation = React.useMemo(
    () =>
      !pendingConfirmationHref
        ? null
        : resolvePendingInlineConfirmation(
            session?.user,
            pendingConfirmationHref
          ),
    // The URL is intentional: dismissing a confirmation consumes
    // its URL/local capability without changing the auth session.
    [pendingConfirmationHref, session?.user]
  );

  const {
    formData,
    currentStep,
    ticketId,
    imageUrl,
    imagePath,
    backImageUrl,
    errorMsg,
    currentView,
    showCreditModal,
    creditModalContext,
    showCreditCheckout,
    showCreditSuccess,
    creditSuccessData,
    creditConfig,
    warnings,
    partialFailure,
    clearWarnings,
    creditPurchaseLoading,
    restorationPending,
    sessionRestoreError,
    sessionRestoreRetryable,
    sessionRecoveryHref,
    sessionSignInHref,
    retryTicketRestoration,
    generateImages,
    handleModifyImage,
    handleCreateTicket,
    getPendingQuoteDesignSpec,
    getPendingQuoteScope,
    handlePrevious,
    handleStartOver,
    handleCreditModalClose,
    handleCreditModalDismiss,
    handlePurchase,
    handleCreateAccountAndBuy,
    showQuoteAccountModal,
    handleQuoteAccountModalDismiss,
    handleCreateAccountAndQuote,
    handleCreditSuccess,
    handleCreditSuccessClose,
    setErrorMsg,
    setShowCreditCheckout,
    showFeedbackModal,
    setShowFeedbackModal,
    hideFeedbackModal,
    sendAgentMessage,
  } = useCreatePage();
  const resumeIntentKey = useCreatePageStore(state =>
    createResumeIntentKey({
      ticketId: state.ticket.id,
      frontImagePath: state.generation.images.front.path,
      agentSessionId: state.agent.sessionId,
      pendingSubmittedMessageId: state.agent.pendingSubmittedMessageId,
    })
  );
  const ticketSubmissionError = useCreatePageStore(
    state => state.ticket.submissionError
  );
  const generationError = useCreatePageStore(
    state => state.generation.errors.message
  );
  // Match the chat shell's renderable-content ownership. A stale entry flag
  // alone does not mount Chat, and the mobile canvas is hidden before entry.
  const hasInlineChat = useCreatePageStore(state =>
    Boolean(
      hasRenderableChatTranscript(state.agent.conversation.messages) ||
      state.generation.images.front.url ||
      state.generation.images.back.url ||
      state.agent.pendingComposerSeed?.trim() ||
      state.agent.pendingComposerRecovery
    )
  );
  const hasInlineErrorSurface = isDesktopWorkspace || hasInlineChat;
  const clearTicketSubmissionError = useCreatePageStore(
    state => state.clearSubmissionError
  );

  const handleErrorClose = React.useCallback(() => {
    clearTicketSubmissionError();
    setErrorMsg(null);
  }, [clearTicketSubmissionError, setErrorMsg]);

  const retrySessionRestore = React.useCallback(() => {
    window.location.reload();
  }, []);

  const handleQuoteResumeUnavailable = React.useCallback(() => {
    const message =
      "Your dress is saved, but we couldn’t reopen your quote. Please try requesting it again.";
    useCreatePageStore.getState().submitError(message);
    setErrorMsg(message);
  }, [setErrorMsg]);

  usePendingQuoteResume({
    authLoading,
    hasAccount: hasVerifiedAccount(session?.user),
    frontImagePath: imagePath,
    resumeKey: resumeIntentKey,
    onResume: handleCreateTicket,
    onIntentUnavailable: handleQuoteResumeUnavailable,
  });

  const handleCreditResumeUnavailable = React.useCallback(
    (credits?: number) => {
      setCheckoutRecoveryCredits(credits ?? null);
      useCreatePageStore.getState().setShowCreditModal("chat_limit");
    },
    []
  );

  usePendingCreditPurchaseResume({
    authLoading,
    hasAccount: hasVerifiedAccount(session?.user),
    restorationPending,
    frontImagePath: imagePath,
    resumeKey: resumeIntentKey,
    onResume: handlePurchase,
    onIntentUnavailable: handleCreditResumeUnavailable,
  });

  const resumeSubmittedMessage = React.useCallback(
    (message: string) =>
      sendAgentMessage(message, undefined, undefined, {
        resumeAfterAuth: true,
      }),
    [sendAgentMessage]
  );
  usePendingSubmittedMessageResume({
    authLoading,
    hasAccount: hasVerifiedAccount(session?.user),
    restorationPending,
    creditsLoading,
    hasCredits: Number(balance ?? 0) > 0,
    onResume: resumeSubmittedMessage,
  });

  useEffect(() => {
    if (authLoading || !pendingInlineConfirmation) return;
    const store = useCreatePageStore.getState();
    if (pendingInlineConfirmation.type === "credit") {
      store.setShowCreditModal("anon_unlock");
      return;
    }
    store.setShowQuoteAccountModal(true);
  }, [authLoading, pendingInlineConfirmation]);

  // The chat subtree reads state via store selectors; only the hook-bound
  // actions travel through context (see ChatActionsProvider).
  const chatActions = React.useMemo(
    () => ({
      sendAgentMessage,
      generateImages,
      modifyImage: handleModifyImage,
      handleCreateTicket,
      handleStartOver,
    }),
    [
      sendAgentMessage,
      generateImages,
      handleModifyImage,
      handleCreateTicket,
      handleStartOver,
    ]
  );

  useEffect(() => {
    if (
      !canResumeCreditGate({
        user: session?.user,
        authLoading,
        creditsLoading,
      })
    ) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get("credit_gate") !== "1") return;

    if (Number(balance ?? 0) <= 0) {
      useCreatePageStore.getState().setShowCreditModal("chat_limit");
    }

    currentUrl.searchParams.delete("credit_gate");
    window.history.replaceState(
      {},
      "",
      currentUrl.pathname + currentUrl.search + currentUrl.hash
    );
  }, [session, authLoading, creditsLoading, balance]);

  const prepareCreditAuthReturn = (
    credits?: number,
    offerToken?: string
  ): string | null => {
    const currentState = useCreatePageStore.getState();
    const handoff = prepareCreditSignInHandoff({
      state: currentState,
      currentHref: window.location.href,
      sourceUserId: needsAnonymousWorkHandoff(session?.user)
        ? session?.user?.id
        : null,
      credits,
      offerToken,
    });
    return handoff?.returnPath ?? null;
  };

  const handleCreditGateSignIn = (credits?: number, offerToken?: string) => {
    const returnPath = prepareCreditAuthReturn(credits, offerToken);
    if (!returnPath) return CREDIT_AUTH_HANDOFF_ERROR;
    redirectToLoginWithDestination(router, returnPath);
  };

  const handleCreditGateSignUp = () => {
    const returnPath = prepareCreditAuthReturn();
    if (!returnPath) return CREDIT_AUTH_HANDOFF_ERROR;
    redirectToLoginWithDestination(router, returnPath, "sign-up");
  };

  const handleCreditGateGoogle = (credits: number, offerToken?: string) => {
    const returnPath = prepareCreditAuthReturn(credits, offerToken);
    if (!returnPath) return CREDIT_AUTH_HANDOFF_ERROR;
    const oauthUrl = new URL("/api/auth/oauth/google", window.location.origin);
    oauthUrl.searchParams.set("next", returnPath);
    window.location.assign(oauthUrl.toString());
  };

  useEffect(() => {
    if (authLoading || !session || !isAnonymous || !imagePath) return;

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get("quote_account") !== "1") return;
    const requestedTicketId = currentUrl.searchParams.get("ticket_id");
    if (requestedTicketId && ticketId !== requestedTicketId) return;

    useCreatePageStore.getState().setShowQuoteAccountModal(true);
    currentUrl.searchParams.delete("quote_account");
    window.history.replaceState(
      {},
      "",
      currentUrl.pathname + currentUrl.search + currentUrl.hash
    );
  }, [authLoading, session, isAnonymous, imagePath, ticketId]);

  const prepareQuoteAuthReturn = (): string | null => {
    const currentState = useCreatePageStore.getState();
    const handoff = prepareQuoteSignInHandoff({
      state: currentState,
      currentHref: window.location.href,
      designSpecOverride: getPendingQuoteDesignSpec?.(),
      quoteScope: getPendingQuoteScope?.(),
      sourceUserId: needsAnonymousWorkHandoff(session?.user)
        ? session?.user?.id
        : null,
    });
    return handoff?.returnPath ?? null;
  };

  const handleQuoteGateSignIn = () => {
    const returnPath = prepareQuoteAuthReturn();
    if (!returnPath) return QUOTE_AUTH_HANDOFF_ERROR;
    redirectToLoginWithDestination(router, returnPath);
  };

  const handleQuoteGateGoogle = () => {
    const returnPath = prepareQuoteAuthReturn();
    if (!returnPath) return QUOTE_AUTH_HANDOFF_ERROR;
    const oauthUrl = new URL("/api/auth/oauth/google", window.location.origin);
    oauthUrl.searchParams.set("next", returnPath);
    window.location.assign(oauthUrl.toString());
  };

  useEffect(() => {
    const checkFeedbackStatus = async () => {
      if (!session || checkingFeedbackStatus) return;
      setCheckingFeedbackStatus(true);
      try {
        const res = await fetch("/api/credits/feedback/status", {
          credentials: "include",
        });
        if (res.ok) {
          const json = await res.json();
          setHasSubmittedFeedback(json.data?.hasSubmittedFeedback ?? false);
        }
      } catch {
        setHasSubmittedFeedback(false);
      } finally {
        setCheckingFeedbackStatus(false);
      }
    };
    if (showCreditModal && hasSubmittedFeedback === null) {
      checkFeedbackStatus();
    }
    if (!showCreditModal && hasSubmittedFeedback !== null) {
      setHasSubmittedFeedback(null);
    }
  }, [showCreditModal, session, hasSubmittedFeedback, checkingFeedbackStatus]);

  useEffect(() => {
    const checkSocialShareStatus = async () => {
      if (!session || checkingSocialShareStatus) return;
      setCheckingSocialShareStatus(true);
      try {
        const res = await fetch("/api/credits/social-share/status", {
          credentials: "include",
        });
        if (res.ok) {
          const json = await res.json();
          setHasShared(json.data?.hasShared ?? false);
        }
      } catch {
        setHasShared(false);
      } finally {
        setCheckingSocialShareStatus(false);
      }
    };
    if (showCreditModal && hasShared === null) {
      checkSocialShareStatus();
    }
    if (!showCreditModal && hasShared !== null) {
      setHasShared(null);
    }
  }, [showCreditModal, session, hasShared, checkingSocialShareStatus]);

  useEffect(() => {
    const fetchTicketCount = async () => {
      if (!session || checkingTicketCount) return;
      setCheckingTicketCount(true);
      try {
        const res = await fetch("/api/tickets/count", {
          credentials: "include",
        });
        if (res.ok) {
          const json = await res.json();
          setTicketCount(Number(json.data?.count ?? 0));
        } else {
          setTicketCount(0);
        }
      } catch {
        setTicketCount(0);
      } finally {
        setCheckingTicketCount(false);
      }
    };

    // Only fetch when the credit modal is actually shown (UX gating only).
    if (showCreditModal && ticketCount === null) {
      fetchTicketCount();
    }
  }, [showCreditModal, session, ticketCount, checkingTicketCount]);

  useEffect(() => {
    const checkShareablePastDesigns = async () => {
      if (!session || checkingShareablePastDesigns) return;
      setCheckingShareablePastDesigns(true);
      try {
        const res = await fetch("/api/credits/social-share/shareable-work", {
          credentials: "include",
        });
        if (res.ok) {
          const json = await res.json();
          const works = Array.isArray(json.data?.works) ? json.data.works : [];
          setHasShareablePastDesigns(works.length > 0);
        } else {
          setHasShareablePastDesigns(false);
        }
      } catch {
        setHasShareablePastDesigns(false);
      } finally {
        setCheckingShareablePastDesigns(false);
      }
    };

    if (showCreditModal && !imageUrl && hasShareablePastDesigns === null) {
      checkShareablePastDesigns();
    }
    if (!showCreditModal && hasShareablePastDesigns !== null) {
      setHasShareablePastDesigns(null);
    }
  }, [
    showCreditModal,
    imageUrl,
    session,
    hasShareablePastDesigns,
    checkingShareablePastDesigns,
  ]);

  const handleFeedbackClick = () => {
    handleCreditModalDismiss();
    if (
      (currentStep === 2 || currentStep === 4) &&
      creditModalContext !== "modify"
    ) {
      handlePrevious();
    }
    setShowFeedbackModal();
  };

  const handleSocialShareClick = () => {
    handleCreditModalDismiss();
    if (
      (currentStep === 2 || currentStep === 4) &&
      creditModalContext !== "modify"
    ) {
      handlePrevious();
    }
    setShowSocialShareModal(true);
  };

  const onSocialShareSuccess = () => window.location.reload();
  const onFeedbackSuccess = () => window.location.reload();

  const socialShareDesignContext = React.useMemo(
    () => ({
      prompt: formData.prompt,
      presetType: formData.preset_type,
      color: formData.color,
      length: formData.length,
      sleeve: formData.sleeve,
      neckline: formData.neckline,
      waistline: formData.waistline,
      silhouette: formData.silhouette,
      fabric: formData.fabric,
    }),
    [
      formData.prompt,
      formData.preset_type,
      formData.color,
      formData.length,
      formData.sleeve,
      formData.neckline,
      formData.waistline,
      formData.silhouette,
      formData.fabric,
    ]
  );

  const socialShareImageUrl =
    currentView === "back" && backImageUrl ? backImageUrl : imageUrl;
  const socialShareBackImageUrl =
    currentView === "back" && backImageUrl ? imageUrl : backImageUrl;

  const sharedModalsProps = {
    // Chat and Design both show workspace errors inline. A duplicate toast
    // covers Apply/Retry on mobile. Keep restoration recovery and errors that
    // are not represented by either workspace error source in the global toast.
    errorMsg:
      hasInlineErrorSurface &&
      ((errorMsg && errorMsg === ticketSubmissionError) ||
        (!restorationPending &&
          !sessionRestoreError &&
          !retryTicketRestoration &&
          errorMsg === generationError))
        ? null
        : errorMsg,
    onErrorClose: handleErrorClose,
    onErrorRetry: retryTicketRestoration,
    warnings,
    partialFailure,
    onWarningsClose: clearWarnings,
    showCreditModal,
    onCreditModalClose: () => {
      setCheckoutRecoveryCredits(null);
      handleCreditModalClose();
    },
    onCreditModalDismiss: () => {
      setCheckoutRecoveryCredits(null);
      handleCreditModalDismiss();
    },
    creditModalContext,
    creditPurchaseLoading,
    checkoutRecoveryCredits,
    onPurchase: async (
      credits: number,
      price: number,
      stripePriceId: string
    ) => {
      await handlePurchase(credits, price, stripePriceId);
      setCheckoutRecoveryCredits(null);
    },
    onCreateAccountAndBuy: handleCreateAccountAndBuy,
    pendingCreditConfirmation:
      pendingInlineConfirmation?.type === "credit"
        ? pendingInlineConfirmation
        : null,
    onFeedbackClick: handleFeedbackClick,
    onSocialShareClick: handleSocialShareClick,
    hasSubmittedFeedback,
    hasShared,
    hasImage: !!imageUrl,
    imageUrl,
    hasShareablePastDesigns,
    canShowFeedbackReward: (ticketCount ?? 0) >= 2,
    canEarnRewards: hasVerifiedAccount(session?.user),
    // "Authenticated" here gates whether we can take money, which needs an
    // ACCOUNT. An anonymous session is signed in but has nothing to attach a
    // purchase to, so it stays on the signed-out side of every branch below.
    isAuthenticated: hasVerifiedAccount(session?.user),
    onSignIn: handleCreditGateSignIn,
    onSignUp:
      !session?.user ||
      (isAnonymous && !creditsLoading && !hasReceivedNewUserBonus)
        ? handleCreditGateSignUp
        : undefined,
    onGoogleContinue: handleCreditGateGoogle,
    showCreditCheckout,
    onCreditCheckoutClose: setShowCreditCheckout,
    onCreditSuccess: handleCreditSuccess,
    creditConfig,
    showCreditSuccess,
    onCreditSuccessClose: handleCreditSuccessClose,
    creditSuccessData: creditSuccessData ?? null,
    showFeedbackModal,
    onFeedbackModalClose: hideFeedbackModal,
    onFeedbackSuccess: onFeedbackSuccess,
    showSocialShareModal,
    onSocialShareModalClose: () => setShowSocialShareModal(false),
    onSocialShareSuccess: onSocialShareSuccess,
    socialShareImageUrl,
    socialShareBackImageUrl,
    socialShareDesignContext,
  };

  return (
    // CreateDressPageClient applies pt-(--navbar-height) on the shell, so this
    // This wrapper must fill the remaining height (flex-1) — not subtract the navbar
    // again with 100dvh - navbar (that caused dead space below the shell).
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden bg-transparent font-sans text-foreground"
      style={{ minHeight: 0 }}
    >
      <Metadata pageKey="create" />

      <ChatActionsProvider actions={chatActions}>
        <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col items-stretch overflow-hidden">
            <CreateAgentLayout
              restorationPending={restorationPending}
              sessionRestoreError={sessionRestoreError}
              onRetrySessionRestore={
                sessionRestoreRetryable ? retrySessionRestore : undefined
              }
              sessionRecoveryHref={sessionRecoveryHref}
              sessionSignInHref={sessionSignInHref}
            />
          </div>
        </div>
      </ChatActionsProvider>

      <CreatePageModals {...sharedModalsProps} />

      <CreateQuoteAccountModal
        open={showQuoteAccountModal}
        imageUrl={imageUrl}
        pendingConfirmationEmail={
          pendingInlineConfirmation?.type === "quote"
            ? pendingInlineConfirmation.email
            : null
        }
        onDismiss={handleQuoteAccountModalDismiss}
        onSignIn={handleQuoteGateSignIn}
        onGoogleContinue={handleQuoteGateGoogle}
        onCreateAccount={handleCreateAccountAndQuote}
      />

      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              name: "Build-a-Dress — Create",
              description: `Design a custom dress with AI guidance. Describe your idea or upload an image, review the front and back, and request a quote. ${CUSTOM_DRESS_STARTING_PRICES}`,
              about: [
                {
                  "@type": "Thing",
                  name: "Custom dress design",
                  description:
                    "Design a custom, made-to-measure dress with AI guidance.",
                },
              ],
            },
          ],
        }}
      />
    </div>
  );
}
